import { fetchOrders } from "src/services/OrderApi";
import { formatDate } from "src/utils/Mapper";
import { getImageUrl } from "src/utils/imageUrl";

const ensureArray = (data) => {
  return Array.isArray(data) ? data : [];
};

// Filter valid orders with safety checks
export const filterValidOrders = (orders) => {
  try {
    const safeOrders = ensureArray(orders);
    return safeOrders.filter((order) => {
      return order && 
             typeof order === 'object' && 
             order.orderStatus !== undefined && 
             order.orderStatus !== null && 
             order.orderStatus !== 9;
    });
  } catch (error) {
    console.error("Error filtering orders:", error);
    return [];
  }
};

// Get order item details with comprehensive error handling
export const getOrderItemDetails = (item, currencyName = "₹") => {
  try {
    if (!item || typeof item !== 'object') {
      return {
        id: null,
        productName: "Unknown Product",
        imageURL: null,
        quantity: 0,
        unitPrice: 0,
        formattedPrice: `${currencyName}0`,
      };
    }

    const quantity = parseInt(item.quantity) || 0;
    const unitPriceWithTax = parseFloat(item.unitPriceWithTax) || 0;
    const totalPriceWithTax = unitPriceWithTax * quantity;

    return {
      id: item.id,
      productName: item.productName,
      imageURL: item.imageURL && item.imageURL.trim()
        ? getImageUrl(item.imageURL.trim())
        : null,
      quantity: quantity,
      unitPrice: parseFloat(item.unitPriceWithoutTax) || 0,
      formattedPrice: `${currencyName}${totalPriceWithTax.toLocaleString('en-IN', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      })}`,
    };
  } catch (error) {
    console.error("Error processing order item details:", error);
    return {
      id: null,
      productName: "Error loading product",
      imageURL: null,
      quantity: 0,
      unitPrice: 0,
      formattedPrice: `${currencyName}0`,
    };
  }
};

// Prepare order data with safety checks
export const prepareOrderData = (order) => {
  try {
    if (!order || typeof order !== 'object') {
      return {
        id: null,
        uuid: "Unknown",
        deliveryDate: "Unknown date",
        lineItems: [],
      };
    }

    return {
      id: order.id,
      uuid: order.uuid,
      deliveryDate: order.CreatedAt ? formatDate(order.CreatedAt) : "Unknown date",
      lineItems: ensureArray(order.lineItems),
    };
  } catch (error) {
    console.error("Error preparing order data:", error);
    return {
      id: null,
      uuid: "Error",
      deliveryDate: "Unknown date",
      lineItems: [],
    };
  }
};

// Fetch all orders with comprehensive error handling
export const fetchAndSetOrders = async (setOrder) => {
  try {
    if (typeof setOrder !== 'function') {
      throw new Error("setOrder must be a function");
    }

    const orderList = await fetchOrders(1, 100);
    
    // Handle different response formats
    let orders = [];
    if (orderList && typeof orderList === 'object') {
      if (Array.isArray(orderList)) {
        orders = orderList;
      } else if (orderList.data && Array.isArray(orderList.data)) {
        orders = orderList.data;
      } else if (orderList.orders && Array.isArray(orderList.orders)) {
        orders = orderList.orders;
      }
    }

    setOrder(ensureArray(orders));
    return orders;
  } catch (error) {
    console.error("Error fetching orders:", error);
    setOrder([]);
    throw error; // Re-throw to allow component to handle it
  }
};

// Fetch filtered orders with comprehensive error handling
export const fetchAndSetFilteredOrders = async (setOrder) => {
  try {
    // First fetch all orders
    const orderList = await fetchOrders(1, 100);
    
    // Handle different response formats
    let allOrders = [];
    if (orderList && typeof orderList === 'object') {
      if (Array.isArray(orderList)) {
        allOrders = orderList;
      } else if (orderList.data && Array.isArray(orderList.data)) {
        allOrders = orderList.data;
      } else if (orderList.orders && Array.isArray(orderList.orders)) {
        allOrders = orderList.orders;
      }
    }

    // Filter valid orders
    const filteredOrders = filterValidOrders(allOrders);
    
    setOrder(filteredOrders);
    return filteredOrders;
  } catch (error) {
    console.error("Error fetching filtered orders:", error);
    setOrder([]);
    throw error; // Re-throw to allow component to handle it
  }
};

